import { v4 as uuidv4 } from "uuid";
import { getPDFBase64 } from "./generateW8BENXML";
import { formatDateForXML, normalizeDateToSlash } from "../../../Utility/utility";
import { getStateCode, parseAddressComponents } from "../../../utils/formUtils";

export const generateW8EXPXML = async (formData: any) => {
  // Helper function to safely get values
  const getValue = (key: string, defaultValue: string = "") => formData[key] || defaultValue;

  // Helper function to safely get boolean values
  const getBooleanValue = (key: string) => (formData[key] ? "true" : "false");

  // Debug specific text field mappings
  const isTruthy = (value: any): boolean => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") {
      const normalized = value.toLowerCase().trim();
      return normalized === "selected" || normalized === "true" || normalized === "yes";
    }
    return false;
  };
  const entityMappings: Array<[string, string]> = [
    ["EXP-Foreign_central_bank_issue", "Foreign central bank of issue"],
    ["EXP-Foreign_government", "Foreign government"],

    ["EXP-Foreign_private_foundation", "Foreign private foundation"],
    ["EXP-Foreign_tax-exempt_organization", "Foreign tax-exempt organization"],
    ["EXP-Government_of_U.S._territory", "Government of a U.S. territory"],
    ["EXP-International_organization", "International organization"],
    ["EXP-Withholding_qualified_holder", "Withholding qualified holder under section 1445"],
  ];
  // Helper function to determine entity type from consolidated field or Chapter 3 Status checkboxes
  const determineEntityType = () => {
    // Check each Chapter 3 Status checkbox using exact form field names FIRST
    for (const [fieldName, entityType] of entityMappings) {
      if (isTruthy(formData[fieldName])) {
        return entityType;
      }
    }

    return ""; // No match — do not send a default value
  };
  const chapter4Mappings: Array<[string, string]> = [
    ["EXP-501c_organization", "501(c) organization"],
    ["EXP-Direct_reporting_NFFE", "Direct reporting NFFE"],
    ["EXP-Exempt_retirement_plan", "Exempt retirement plan"],
    [
      "EXP-4_foreign_government_central_bank",
      "Foreign government (including a political subdivision), government of a U.S. territory, or foreign central bank of issue",
    ],
    ["EXP-4_international_organization", "International organization"],
    ["EXP-Nonreporting_IGA_FFI", "Nonreporting IGA FFI"],
    ["EXP-Participating_FFI", "Participating FFI"],
    ["EXP-Passive_NFFE", "Passive NFFE"],
    ["EXP-Registered_deemed_compliant_FFI", "Registered deemed-compliant FFI"],
    ["EXP-Reporting_Model1_FFI", "Reporting Model 1 FFI"],
    ["EXP-Reporting_Model2_FFI", "Reporting Model 2 FFI"],
    ["EXP-Sponsored_direct_reporting_NFFE", "Sponsored direct reporting NFFE"],
    ["EXP-Territory_financial_institution", "Territory financial institution"],
  ];
  // Helper function to determine FATCA status from consolidated field or Chapter 4 checkboxes
  const determineFATCAStatus = () => {
    for (const [fieldName, chapter4Status] of chapter4Mappings) {
      if (isTruthy(formData[fieldName])) {
        return chapter4Status;
      }
    }
    return ""; // No match — do not send a default value
  };

  const currentDate = new Date().toISOString();

  // Generate related documents XML
  const generateRelatedDocumentsXml = async () => {
    let relatedDocumentsXml = "";

    // Helper function to get document type info
    const getDocumentTypeInfo = (documentType: string) => {
      const documentTypeMapping: Record<string, { typeId: string; type: string }> = {
        partnership_agreement: {
          typeId: "74B29767-B166-49A1-90C6-04D919FFF021",
          type: "Partnership Agreement",
        },
        trust_deed: {
          typeId: "E0E34437-CC13-487C-863E-248204E61086",
          type: "Trust Deed or Certified Extracts",
        },
        certificate_registration: {
          typeId: "F0B2D5EC-5C24-4FE5-A571-2610610FA3D1",
          type: "Certificate of Registration",
        },
        certificate_incorporation: {
          typeId: "F113A468-ADE7-4C20-82C4-33A496456F54",
          type: "Certificate of Incorporation",
        },
        armed_forces_card: {
          typeId: "5EBD9B1F-BEB2-4BB5-BCB9-35C105F278A5",
          type: "Armed Forces Card",
        },
        limitations_benefits: {
          typeId: "5309E91F-6B24-4183-9C7F-3A062658E625",
          type: "Limitations of Benefits Statement",
        },
        election_form: {
          typeId: "DB6649B1-F4BE-41D5-AAAD-4588B253C39D",
          type: "Election form - line 9 option",
        },
        public_register: {
          typeId: "58740ED7-F2EF-481D-B0FA-4703F257B1C6",
          type: "Public Register Extracts",
        },
        driving_license: {
          typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8",
          type: "Driving License",
        },
        driverLicense: { typeId: "9FECCC5A-4CC3-46C2-AB7D-53FB2E3DB0B8", type: "Driving License" },
        state_eu: { typeId: "D525BDE1-E0A2-4F2A-B2CE-5AD75383264E", type: "State of the EU" },
        email: { typeId: "C2676B9E-BCFA-497E-8588-6B8B1DA00914", type: "eMail" },
        retroactive_statement: {
          typeId: "3DC45284-ACA4-494B-AA1F-6EBA8736F9E1",
          type: "Retroactive Statement",
        },
        birth_certificate: {
          typeId: "70612B92-D0F3-4347-9CC3-912B9597C800",
          type: "Birth Certificate",
        },
        passport: { typeId: "70825FED-AEBF-4EB8-949D-998D69D718F9", type: "Passport" },
        withholding_statement: {
          typeId: "872ACA95-E43E-4EFA-A494-9A4A97356365",
          type: "Withholding Statement",
        },
        email_alt: { typeId: "325FFDDE-2558-4E32-B7DA-A67435BF59F9", type: "eMail" },
        other: { typeId: "E697B03A-91D1-4CEB-8EC4-A7A4E53E5B30", type: "Other" },
        hybrid_status: {
          typeId: "5A447556-7724-484B-A6D4-BAE392B8FF22",
          type: "Hybrid Status - Supporting Documentation",
        },
        notional_principle: {
          typeId: "BB810C57-8652-4431-BEB2-BE50057C17DC",
          type: "Notional principle contracts statement",
        },
        connected_documentation: {
          typeId: "ACCDB44A-2518-4B06-95C2-BF837E64E343",
          type: "Connected documentation",
        },
        national_id_member: {
          typeId: "4EA07713-50FD-4427-AB0A-C3972E3A7BB0",
          type: "National ID Card of A Member",
        },
        form_w4: { typeId: "D9AFD794-205A-45C3-A64A-C5219F20CBB2", type: "Form W-4" },
        proof_residency: {
          typeId: "F1BC6DC5-19C6-441F-A133-CEDE22778968",
          type: "Proof of Residency Document",
        },
        affidavit: { typeId: "B6477F29-2FE4-4CC0-A58E-CF7B91F791E7", type: "Affidavit" },
        national_id: { typeId: "405AE4A8-A1FA-45EB-9449-D1C76EC101FB", type: "National ID Card" },
        effectively_connected: {
          typeId: "BD2028C8-56A5-4404-895A-D36F42535DDB",
          type: "Effectively connected income statement - line 9 option",
        },
        power_attorney: {
          typeId: "59AC92CD-62C8-46DC-BC33-DBAFA0716242",
          type: "Power of attorney statement",
        },
        us_counsel: {
          typeId: "B6A5ABBA-E61E-4C5C-9B43-E051D75A26F6",
          type: "U.S. counsel opinion statement",
        },
        work_visa: { typeId: "1F7944EA-CF76-40FB-B182-E3D1DE59A74A", type: "Work visa" },
        expatriate: {
          typeId: "7AE8E536-21FD-46A2-93B7-EE99477D7DE2",
          type: "Expatriate Documentation",
        },
      };

      // Normalize the document type by removing common prefixes and converting to lowercase
      const normalizedType = documentType
        .toLowerCase()
        .replace(/^idDocument\./, "")
        .replace(/\.pdf$/, "");

      // Try direct match first
      if (documentTypeMapping[normalizedType]) {
        return documentTypeMapping[normalizedType];
      }

      // Try partial matches for common document types
      if (normalizedType.includes("passport")) {
        return documentTypeMapping["passport"];
      }
      if (normalizedType.includes("driver") || normalizedType.includes("license")) {
        return documentTypeMapping["driving_license"];
      }
      if (normalizedType.includes("national") && normalizedType.includes("id")) {
        return documentTypeMapping["national_id"];
      }

      // Default to 'Other' if no match found
      return documentTypeMapping["other"];
    };

    // Handle single document case from original code
    if (formData.idDocumentUrl) {
      const docContent = await getPDFBase64(formData.idDocumentUrl);
      const docTypeInfo = getDocumentTypeInfo(formData.idDocumentType || "other");

      relatedDocumentsXml += `
      <RelatedDocument>
        <ID>${uuidv4()}</ID>
        <Type>${docTypeInfo.type}</Type>
        <TypeID>${docTypeInfo.typeId}</TypeID>
        <File>
          <Name>${formData.idDocumentType || "id_document"}.pdf</Name>
          <Data>${docContent}</Data>
        </File>
      </RelatedDocument>`;
    }

    // Handle multiple documents if provided in docs array
    if (formData.docs && Array.isArray(formData.docs)) {
      for (const doc of formData.docs) {
        if (doc.url) {
          // Make sure to await the PDF base64 conversion
          const docContent = await getPDFBase64(doc.url);
          const docTypeInfo = getDocumentTypeInfo(doc.type || "other");

          relatedDocumentsXml += `
          <RelatedDocument>
            <Type>${docTypeInfo.type}</Type>
            <TypeID>${docTypeInfo.typeId}</TypeID>
            <File>
              <Name>${doc.name || "document.pdf"}</Name>
              <Data>${docContent}</Data>
            </File>
          </RelatedDocument>`;
        }
      }
    }
    // Handle withholding statement document (attached to first form matching first row of excel)
    if (formData.withholdingStatementUrl) {
      try {
        const whsContent = await getPDFBase64(formData.withholdingStatementUrl);
        const whsTypeInfo = getDocumentTypeInfo("withholding_statement");

        relatedDocumentsXml += `
      <RelatedDocument>
        <Type>${whsTypeInfo.type}</Type>
        <TypeID>${whsTypeInfo.typeId}</TypeID>
        <File>
          <Name>Withholding Statement.pdf</Name>
          <Data>${whsContent}</Data>
        </File>
      </RelatedDocument>`;
      } catch (err) {
        console.error("Failed to attach Withholding Statement:", err);
      }
    }
    return relatedDocumentsXml;
  };
  const relatedDocumentsXml = await generateRelatedDocumentsXml();

  // Generate original file XML if viewPdf is provided
  const generateOriginalFileXml = async () => {
    const viewPdfUrl = getValue("viewPdf", "");
    if (viewPdfUrl) {
      try {
        const originalFileContent = await getPDFBase64(viewPdfUrl, formData.fileImportId);
        const fileName = viewPdfUrl.split("/").pop() || "original_document.pdf";

        return `
        <OriginalFile>
          <Name>${fileName}</Name>
          <Data>${originalFileContent}</Data>
        </OriginalFile>`;
      } catch (error) {
        console.error("Error converting original PDF to base64:", error);
        return "<OriginalFile></OriginalFile>";
      }
    }
    return "<OriginalFile></OriginalFile>";
  };

  const originalFileXml = await generateOriginalFileXml();

  // Debug the actual function calls before XML generation
  // console.log("📋 About to call determineEntityType()...");
  const entityTypeResult = determineEntityType();
  // console.log("✅ entityTypeResult:", entityTypeResult);

  // console.log("📋 About to call determineFATCAStatus()...");
  const fatcaStatusResult = determineFATCAStatus();
  // console.log("✅ fatcaStatusResult:", fatcaStatusResult);

  // Parse permanent address components if EXP-Mailing_address_city_state_postal is not empty
  let expMailingAddressParsed = { city: "", state: "", postalCode: "" };
  const expMailingCityAddress = getValue("EXP-Mailing_address_city_state_postal", "");
  if (expMailingCityAddress && expMailingCityAddress.trim() !== "") {
    expMailingAddressParsed = parseAddressComponents(expMailingCityAddress);
  }

  // Parse permanent city address if available and not already parsed
  let permanentCityAddressField: { city?: string; state?: string; postalCode?: string } = {};
  const permanentCityAddress = getValue("EXP-Permanent_address_city_state_postal", "");
  if (permanentCityAddress && permanentCityAddress.trim() !== "") {
    permanentCityAddressField = parseAddressComponents(permanentCityAddress);
  }

  const xml = `
<W_8EXP_OCT_2023>
    <UniqueIdentifier>${getValue("UniqueIdentifier")}</UniqueIdentifier>
    <UIDreferenceline>False</UIDreferenceline>
    <Instructor_Identifier>${getValue("entityUniqueIdentifier")}</Instructor_Identifier>
    <BusinessUnit>${getValue("BusinessUnit", "")}</BusinessUnit>
    <ElectronicRecipientStatement>True</ElectronicRecipientStatement>
    <ElectronicRecipientStatementDate>${formatDateForXML(getValue("signature_date") || currentDate)}</ElectronicRecipientStatementDate>
    		        <Entity></Entity>
    <CommonInfo>
      <EntityTypes>
        <EntityType>${entityTypeResult}</EntityType>
      </EntityTypes>
     
      <EntityClassification/>
      <InitialEntityTypeSelection>Non U.S. Entity</InitialEntityTypeSelection>
      <FATCAEntityType>${fatcaStatusResult}</FATCAEntityType>
      <Individual>
        <FirstName />
        <LastName />
        <CountryOfBirth />
        <CountryOfBirthOther />
        <CountryOfCitizenshipForTax />
        <CountryOfCitizenshipForTaxOther />
        <CityTownOfBirth />
        <PlaceofBirth />
        <AdditionalTaxJurisdictions />
      </Individual>
      <Business>
        <Name>${getValue("EXP-Organization_name") || getValue("Name of organization")}</Name>
        <Country>${getValue("Country_of_incorporation_organization") || getValue("Country_of_incorporation/organization") || getValue("Country_of_incorporation_organization")}</Country>
        <CountryOther>${getValue("Other")}</CountryOther>
        <DoingBusinessAsName/>
        <DREFATCAEntitytype></DREFATCAEntitytype>
        <JurisdictionTaxCountry/>
        <SponsoringEntityName>${getValue("EXP-22_sponsoring_entity_name") || getValue("EXP-22_sponsoring_entity_name")}</SponsoringEntityName>
        <SponsoringEntityGIIN/>
        <TrusteeName/>
        <TrusteeGIIN/>
        <AdditionalTaxJurisdictions/>
        <IsSpecifiedUSPerson>False</IsSpecifiedUSPerson>
        <SpecifiedPersonUS>NA</SpecifiedPersonUS>
        <IsSpecifiedUKPerson>False</IsSpecifiedUKPerson>
        <SpecifiedPersonUK>NA</SpecifiedPersonUK>
        <IsSpecifiedCRSPerson>False</IsSpecifiedCRSPerson>
        <SpecifiedPersonCRS>NA</SpecifiedPersonCRS>
      </Business>
      <PermanentAddress>
        <Line1>${getValue("Permanent residence address")}</Line1>
        <Line2>${getValue("Permanent address line 2")}</Line2>
        <Line3>${getValue("Permanent address line 3")}</Line3>
        <City_Town>${getValue("Permanent address city") || permanentCityAddressField.city}</City_Town>
        <State_Province>${getStateCode(getValue("Permanent address state NonUS") || getStateCode(getValue("Permanent address state USCA")) || getStateCode(permanentCityAddressField.state || ""))}</State_Province>
        <Country>${getValue("Permanent address country")}</Country>
        <CountryOther/>
        <Zip_PostalCode>${getValue("Permanent address zip") || permanentCityAddressField.postalCode}</Zip_PostalCode>
        <IsCare_of_address>${getValue("Permanent address is care of") === "yes" ? "True" : "False"}</IsCare_of_address>
        <IsP_O_Box>${getValue("Permanent address is po box") === "yes" ? "True" : "False"}</IsP_O_Box>
        <IsRuralRoute>${getValue("Permanent address is rural route") === "yes" ? "True" : "False"}</IsRuralRoute>
      </PermanentAddress>
      <MailingAddress>
        <Line1>${getValue("EXP-Mailing_address_street")}</Line1>
        <Line2>${getValue("Mailing address line 2")}</Line2>
        <Line3>${getValue("Mailing address line 3")}</Line3>
        <City_Town>${getValue("EXP-Mailing_address_city") || expMailingAddressParsed.city}</City_Town>
        <State_Province>${getStateCode(getValue("EXP-Mailing_address_state_NonUS") || getStateCode(getValue("EXP-Mailing_address_state_USCA")) || getStateCode(expMailingAddressParsed.state))}</State_Province>
        <Country>${getValue("EXP-Mailing_address_country")}</Country>
        <CountryOther/>
        <Zip_PostalCode>${getValue("Mailing address zip") || expMailingAddressParsed.postalCode}</Zip_PostalCode>
        <IsCare_of_address>${getValue("Mailing address is care of") === "yes" ? "True" : "False"}</IsCare_of_address>
        <IsP_O_Box>${getValue("Mailing address is po box") === "yes" ? "True" : "False"}</IsP_O_Box>
        <IsRuralRoute>${getValue("Mailing address is rural route") === "yes" ? "True" : "False"}</IsRuralRoute>
      </MailingAddress>
      <ContactInfo>
        <FirstName>${getValue("Print_name").split(" ")[0] || ""}</FirstName>
        <LastName>${getValue("Print_name").split(" ").slice(1).join(" ") || ""}</LastName>
        <Email/>
        <SignatoryEmail/>
        <TelephoneCountry/>
        <TelephoneNumber/>
        <CellNumber/>
      </ContactInfo>
      <CitizenshipInfo>
        <IsUSPerson />
        <IsUSGreenCardHolder />
        <IsDualCitizenshipHolder />
        <DoesDualCitizenshipIncludeAmerica />
        <IsBornOrganisedInUS />
        <HasRenouncedCitizenship />
        <DateRenouncedCitizenship />
        <AreCountryForTaxAndBirthDifferent />
        <InUSA31DaysCurrentYear />
      </CitizenshipInfo>
      <IsOnBehalfOfSomeoneElse>False</IsOnBehalfOfSomeoneElse>
      <Proxy>
        <FirstName/>
        <LastName/>
        <Capacity/>
        <CapacityOther/>
        <PrimaryContactNumber/>
        <AlternativeNumberCountry/>
        <AlternativeNumber/>
      </Proxy>
      <NoUSSiDeclarationConfirmed />
      <NoUSSiDeclarationPart2_3Skipped>False</NoUSSiDeclarationPart2_3Skipped>
      <IgnoredRules>
        <IgnoredRule>
          <Rule>TIN128</Rule>
          <Comment />
        </IgnoredRule>
      </IgnoredRules>
      <NonUSIndividual_IsClaimingECI />
      <NonUSIndividual_IsForIndependentPersonalServices />
      <NonUSIndividual_IsPublicEntertainer />
      <NonUSIndividual_IsResidentNoSpecificProvision />
      <NonUSIndividual_IsForDependentPersonalServices />
      <NonUSIndividual_IsForScholarshipFellowshipAndPersonalServicesIncome />
      <NonUSIndividual_PartOfWhichNotClaimingTaxTreatyWithholdingExemption />
      <NonUSIndividual_IsNotClaimingTaxTreatyWithholdingExemption />
      <NonUSIndividual_IsForScholarshipFellowshipNoPersonalServicesIncome />
      <NonUSIndividual_IsForOnlyForeignStatusOrTreatyBenefits />
      <SubmissionGroupId />
      <SelectedOptionsonGuide4 />
    </CommonInfo>
    <TaxpayerIdentification>
      <Type>EIN</Type>
      <GIINAppliedFor>${getBooleanValue("GIIN Applied For")}</GIINAppliedFor>
      <GIINNotApplicable>False</GIINNotApplicable>
      <GIIN>${getValue("GIIN")}</GIIN>
      <EIN>${getValue("U.S. taxpayer identification number")}</EIN>
      <ForeignTaxpayerId>${getValue("Foreign tax identifying number")}</ForeignTaxpayerId>
      <TINFormatNotAvail>${getBooleanValue("FTIN not legally required")}</TINFormatNotAvail>
      <ForeignTaxpayerCountry>${getValue("Country_of_incorporation_organization") || getValue("Country_of_incorporation/organization")}</ForeignTaxpayerCountry>
      <ForeignTinUnavailable>False</ForeignTinUnavailable>
      <ForeignTinUnavailableReason />
      <IsAppliedForTIN>False</IsAppliedForTIN>
      <UnexpectedTINTypeExplanation />
      <UnexpectedTINTypeExplanationConfirmed />
      <UnexpectedTINTypeContentBlock />
      <CheckifFTINnotlegallyrequired>${getBooleanValue("FTIN not legally required")}</CheckifFTINnotlegallyrequired>
      <IsFurtherExplanation>False</IsFurtherExplanation>
      <WrittenStatementReasons />
      <WrittenStatementReasonsAdditionalInformation />
      <TINCheck>
        <IsCheckPreformed>False</IsCheckPreformed>
        <IsSuccessful>False</IsSuccessful>
        <Date>${currentDate}</Date>
      </TINCheck>
    </TaxpayerIdentification>
    <SignInfo>
      <IsSigned>${getBooleanValue("isMapDone") === "true" ? (getValue("isSigned") === "yes" ? "true" : "false") : getValue("Signature") !== "" ? true : false}</IsSigned>
      <By>${getValue("Print_name")}</By>
      <Capacity>${getValue("Signing_capacity")}</Capacity>
      <Date>${normalizeDateToSlash(getValue("Signature_date"))}</Date>
      <DateOnForm>${getValue("Signature_date_on_form") || getValue("Signature_date")}</DateOnForm>
      <UserLocalTime>${new Date().toLocaleString()}</UserLocalTime>
      <SignatureUnreadable>${getBooleanValue("Signature_unreadable")}</SignatureUnreadable>
      <UserLocalTimeZone>Coordinated Universal Time</UserLocalTimeZone>
      <EnteredConfirmationCode />
    </SignInfo>
    <Tag>
      <SubmissionDate></SubmissionDate>
      <IsFromComplyExchangeService>True</IsFromComplyExchangeService>
      <IsFromBureau>False</IsFromBureau>
      <IsPaperForm>False</IsPaperForm>
      <IsE_form>True</IsE_form>
      <RetroactiveStatement>
        <IsSigned>${getValue("isSigned") === "yes" ? "True" : "False"}</IsSigned>
        <SignedBy>${getValue("Print_name")}</SignedBy>
        <SignDate>${formatDateForXML(getValue("signature_date"))}</SignDate>
        <EffectiveDate></EffectiveDate>
        <ChangeinCircumstanceDate></ChangeinCircumstanceDate>
        <ReasonForChange/>
        <Explanation/>
        <RetroSignBy/>
        <StatementText>I declare, under penalties of perjury, that I have examined the Form W-8EXP submission that I am providing and that the information and certifications contained therein remain the same and unchanged throughout the period beginning on ${currentDate} to the date of the submission here and that they were true, correct and complete during the entire period.</StatementText>
      </RetroactiveStatement>
    </Tag>
    <RelatedDocuments>
      ${relatedDocumentsXml}
    </RelatedDocuments>
    ${originalFileXml}
    <AgentLogo />
    <FATCAEntityType></FATCAEntityType>
    	        <ReferenceNumbers>${getValue("EXP-Reference_numbers")}</ReferenceNumbers>
    <Line10a>${getBooleanValue("EXP-10A_Foreign_government")}</Line10a>
    <Line10b>${getBooleanValue("EXP-10B_Integral_part")}</Line10b>
    <Line10bCountry>${getValue("EXP-10B_Integral_part_government_name") || getValue("EXP-10B_Government_name")}</Line10bCountry>
    <Line10c>${getBooleanValue("EXP-10C_Controlled_entity")}</Line10c>
    <Line10cCountry>${getValue("EXP-10C_Controlled_entity_government_name") || getValue("EXP-10C_Government_name")}</Line10cCountry>
    <Line10d>${getBooleanValue("EXP-10D_Not_commercial_activities")}</Line10d>
    <Line10e>${getBooleanValue("EXP-10E_Central_bank_not_us_commercial")}</Line10e>
    <Line11>${getBooleanValue("EXP-11_International_organization")}</Line11>
    <Line12>${getBooleanValue("EXP-12_Foreign_central_bank")}</Line12>
    <Line13a>${getBooleanValue("EXP-13A_Irs_determination")}</Line13a>
    <Line13aDate>${getValue("EXP-13A_tax_exempt_determination_letter_date") || getValue("EXP-13A_Text")}</Line13aDate>
    <Line13b>${getBooleanValue("EXP-13B_Us_counsel_opinion")}</Line13b>
    <Line13c>${getBooleanValue("EXP-13C_Not_private_foundation")}</Line13c>
    <Line13d>${getBooleanValue("EXP-13D_Is_private_foundation")}</Line13d>
    <Line14>${getBooleanValue("EXP-14_Us_territory_government")}</Line14>
    <Line15a>${getBooleanValue("EXP-15A_Qualified_holder")}</Line15a>
    <Line15b>${getBooleanValue("EXP-15B_Foreign_partnership_qualified_holders")}</Line15b>
    <Line16>${getBooleanValue("EXP-16_Nonreporting_IGA_FFI")}</Line16>
    <Line16IGACountry>${getValue("EXP-16_nonreporting_iga_ffi_country")}</Line16IGACountry>
     <Line16IGACountryOther></Line16IGACountryOther>
    <Line16EntityClass>${getValue("EXP-16_Treated_text") || getValue("EXP-16_Treated_as") || getValue("EXP-16_entity_type") || getValue("EXP-16_nonreporting_iga_treated") || (formData["EXP-16_Nonreporting_IGA_FFI"] === "selected" ? "Nonreporting IGA FFI" : "")}</Line16EntityClass>

    <Line16EntityClassValue>${getValue("EXP-16_nonreporting_iga_ffi_treatment")}</Line16EntityClassValue>
    <Line16GIIN>${getValue("EXP-16_nonreporting_iga_ffi_giin")}</Line16GIIN>
    <Line17>${getBooleanValue("EXP-17_Territory_financial_institution")}</Line17>
    <Line18>${getBooleanValue("EXP-18_Foreign_government_fatca")}</Line18>
    <Line19a>${getBooleanValue("EXP-19A_Retirement_plan_employees")}</Line19a>
    <Line19b>${getBooleanValue("EXP-19B_Entity_identitfier")}</Line19b>
    <Line20>${getBooleanValue("EXP-20_501c_Organization_fatca")}</Line20>
    <Line21a>${getBooleanValue("EXP-21A_Passive_nffe_certification")}</Line21a>
    <Line21b>${getBooleanValue("EXP-21B_No_substantial_us_owners")}</Line21b>
    <Line21c>${getBooleanValue("EXP-21C_Has_substantial_us_owners")}</Line21c>
    <Line22>${getBooleanValue("EXP-22_Sponsored_direct_reporting")}</Line22>
    <Line22SponsoringEntityName>${getValue("EXP-22_sponsoring_entity_name") || getValue("EXP-22_sponsoring_entity_name")}</Line22SponsoringEntityName>
    <SubstituteFormStatement>The Internal Revenue Service does not require your consent to any provisions of this document other than the certifications required to establish your exempt status.</SubstituteFormStatement>
    <SignatureCertify>${getBooleanValue("Signing_capacity")}</SignatureCertify>
</W_8EXP_OCT_2023>`;
  console.log("Generated W-8EXP XML:", xml);
  return xml;
};

//  <EntityClassification>${determineFATCAStatus()}</EntityClassification>
